import {
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
  TextareaHTMLAttributes,
  useId,
} from 'react';
import {
  getOtsFormControlClassName,
  OtsFormField,
  OtsFormFieldSize,
} from './OtsFormField';

export type OtsTextInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'size'
> & {
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  size?: OtsFormFieldSize;
  /** Applies layout styles to the complete label/control/error field. */
  containerClassName?: string;
};

/** Theme-aware text foundation shared by full pages and compact GWT mounts. */
export const OtsTextInput = forwardRef<HTMLInputElement, OtsTextInputProps>(
  function OtsTextInput(
    {
      id: idProp,
      label,
      description,
      error,
      size = 'default',
      required,
      className,
      containerClassName,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref
  ) {
    const generatedId = useId();
    const id = idProp ?? `ots-text-${generatedId}`;
    const descriptionId = description ? `${id}-description` : undefined;
    const errorId = error ? `${id}-error` : undefined;
    const describedBy = [ariaDescribedBy, descriptionId, errorId]
      .filter(Boolean)
      .join(' ');

    return (
      <OtsFormField
        htmlFor={id}
        label={label}
        description={description}
        descriptionId={descriptionId}
        error={error}
        errorId={errorId}
        required={required}
        size={size}
        className={containerClassName}
      >
        <input
          {...props}
          ref={ref}
          id={id}
          required={required}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={describedBy || undefined}
          className={getOtsFormControlClassName(size, className)}
        />
      </OtsFormField>
    );
  }
);

export type OtsTextAreaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'size'
> & {
  label: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  size?: OtsFormFieldSize;
  /** Applies layout styles to the complete label/control/error field. */
  containerClassName?: string;
};

/** Multiline companion to OtsTextInput, including compact density and theme tokens. */
export const OtsTextArea = forwardRef<HTMLTextAreaElement, OtsTextAreaProps>(
  function OtsTextArea(
    {
      id: idProp,
      label,
      description,
      error,
      size = 'default',
      required,
      className,
      containerClassName,
      rows = 4,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref
  ) {
    const generatedId = useId();
    const id = idProp ?? `ots-textarea-${generatedId}`;
    const descriptionId = description ? `${id}-description` : undefined;
    const errorId = error ? `${id}-error` : undefined;
    const describedBy = [ariaDescribedBy, descriptionId, errorId]
      .filter(Boolean)
      .join(' ');

    return (
      <OtsFormField
        htmlFor={id}
        label={label}
        description={description}
        descriptionId={descriptionId}
        error={error}
        errorId={errorId}
        required={required}
        size={size}
        className={containerClassName}
      >
        <textarea
          {...props}
          ref={ref}
          id={id}
          rows={rows}
          required={required}
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={describedBy || undefined}
          className={getOtsFormControlClassName(size, className)}
        />
      </OtsFormField>
    );
  }
);
